import { forwardRef } from 'react';
import { MoreHorizontal, Share2, Zap, FileText, Table, Trash2 } from 'lucide-react';

export interface MenuActions {
  onShare?: () => void;
  onCreateWorkflow?: () => void;
  onExportCSV?: () => void;
  onExportExcel?: () => void;
  onDelete?: () => void;
}

interface ChatOverflowMenuProps {
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  actions: MenuActions;
  position?: 'above' | 'below';
  buttonSize?: 'sm' | 'md';
}

/**
 * Overflow menu for chat panel actions (share, export, delete, etc.)
 * Used in both chat panel header and bottom bar
 */
export const ChatOverflowMenu = forwardRef<HTMLDivElement, ChatOverflowMenuProps>(
  ({ isOpen, onToggle, onClose, actions, position = 'below', buttonSize = 'sm' }, ref) => {
    const { onShare, onCreateWorkflow, onExportCSV, onExportExcel, onDelete } = actions;

    const handleAction = (action: (() => void) | undefined) => {
      if (action) {
        action();
        onClose();
      }
    };

    const buttonSizeClasses = buttonSize === 'sm'
      ? 'w-7 h-7'
      : 'w-8 h-8';

    const menuPositionClasses = position === 'above'
      ? 'bottom-full right-0 mb-2'
      : 'top-full right-0 mt-1';

    return (
      <div className="relative" ref={ref}>
        <button
          type="button"
          onClick={onToggle}
          className={`flex items-center justify-center ${buttonSizeClasses} text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors`}
          title="More actions"
        >
          <MoreHorizontal className="w-4 h-4" />
        </button>

        {isOpen && (
          <div className={`absolute ${menuPositionClasses} w-44 bg-white border border-gray-200 rounded-xl shadow-lg z-50 overflow-hidden py-1`}>
            {onShare && (
              <button
                type="button"
                onClick={() => handleAction(onShare)}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-[13px] text-gray-700 hover:bg-gray-50 text-left transition-colors"
              >
                <Share2 className="w-4 h-4 text-gray-400" />
                Share
              </button>
            )}
            {onCreateWorkflow && (
              <button
                type="button"
                onClick={() => handleAction(onCreateWorkflow)}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-[13px] text-gray-700 hover:bg-gray-50 text-left transition-colors"
              >
                <Zap className="w-4 h-4 text-gray-400" />
                Create Workflow
              </button>
            )}
            {(onShare || onCreateWorkflow) && (onExportCSV || onExportExcel) && (
              <div className="h-px bg-gray-100 my-1" />
            )}
            {onExportCSV && (
              <button
                type="button"
                onClick={() => handleAction(onExportCSV)}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-[13px] text-gray-700 hover:bg-gray-50 text-left transition-colors"
              >
                <FileText className="w-4 h-4 text-gray-400" />
                Export CSV
              </button>
            )}
            {onExportExcel && (
              <button
                type="button"
                onClick={() => handleAction(onExportExcel)}
                className="w-full flex items-center gap-2.5 px-3 py-2 text-[13px] text-gray-700 hover:bg-gray-50 text-left transition-colors"
              >
                <Table className="w-4 h-4 text-gray-400" />
                Export Excel
              </button>
            )}
            {onDelete && (
              <>
                <div className="h-px bg-gray-100 my-1" />
                <button
                  type="button"
                  onClick={() => handleAction(onDelete)}
                  className="w-full flex items-center gap-2.5 px-3 py-2 text-[13px] text-red-600 hover:bg-red-50 text-left transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete Dataset
                </button>
              </>
            )}
          </div>
        )}
      </div>
    );
  }
);

ChatOverflowMenu.displayName = 'ChatOverflowMenu';
